"use client"

import Link from "next/link"
import { useId } from "react"
import type { UserView } from "../lib/user-view.ts"
import { useManagedDialog } from "./use-managed-dialog.ts"

export type NotificationEntry = Extract<UserView, { readonly kind: "notifications" }>["notifications"][number]

const dateTimeFormatter = new Intl.DateTimeFormat("ko-KR", { dateStyle: "long", timeStyle: "short" })

export const notificationDestinationHref = (notification: NotificationEntry): string | null => {
  if (notification.destination === "support" && notification.destinationReferenceId !== null) return `/support/inquiries/${notification.destinationReferenceId}`
  if (notification.destination === "orders" && notification.destinationReferenceId !== null) return `/orders/${notification.destinationReferenceId}`
  if (notification.destination === "vouchers") return "/vouchers"
  if (notification.destination === "balance") return "/balance/history"
  return null
}

const destinationLabel = (notification: NotificationEntry): string | null => {
  if (notification.destination === "orders") return "주문 상세 보기"
  if (notification.destination === "vouchers") return "내 상품권 보기"
  if (notification.destination === "balance") return "이용 내역 보기"
  return null
}

export const NotificationDetailSheet = ({ notification, onClose }: { readonly notification: NotificationEntry | null; readonly onClose: () => void }) => {
  const headingId = useId()
  const { cancel, dialogRef, onDialogClose } = useManagedDialog(notification !== null, onClose)

  const href = notification === null ? null : notificationDestinationHref(notification)
  const label = notification === null ? null : destinationLabel(notification)

  return (
    <dialog
      aria-labelledby={headingId}
      className="notification-detail-dialog"
      data-notification-detail-sheet
      onCancel={(event) => {
        event.preventDefault()
        cancel()
      }}
      onClose={onDialogClose}
      ref={dialogRef}
    >
      {notification === null ? null : (
        <div className="notification-detail-content">
          <header className="notification-detail-heading">
            <div>
              <span className="notification-detail-kicker">알림 상세</span>
              <h2 id={headingId}>{notification.title}</h2>
            </div>
            <button aria-label="알림 상세 닫기" className="notification-detail-close" onClick={cancel} type="button">
              ×
            </button>
          </header>
          <p className="notification-detail-body">{notification.body}</p>
          <time className="notification-detail-time" dateTime={notification.occurredAt}>
            {dateTimeFormatter.format(new Date(notification.occurredAt))}
          </time>
          {href === null || label === null ? null : (
            <Link className="notification-detail-link" href={href} onClick={cancel}>
              {label}
            </Link>
          )}
        </div>
      )}
    </dialog>
  )
}
